import * as React from "react";
import { useList } from "@refinedev/core";
import type { CrudFilter } from "@refinedev/core";
import { dataProvider } from "@/providers/dataProvider";
import { SkeletonTable } from "@/components/SkeletonTable";
import { EmptyState } from "@/components/EmptyState";
import { ErrorBanner } from "@/components/ErrorBanner";
import { CursorPagination } from "@/components/CursorPagination";
import { ConfirmModal } from "@/components/ConfirmModal";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PlaceStatus = "active" | "hidden";

export type AdminPlace = {
  id: string;
  name: string;
  isHidden: boolean;
  cityId: string | null;
  countryId: string | null;
};

// ---------------------------------------------------------------------------
// PlaceStatusPill
// ---------------------------------------------------------------------------

const PLACE_STATUS_LABELS: Record<PlaceStatus, string> = {
  active: "Активно",
  hidden: "Скрыто",
};

const PLACE_STATUS_CLASS: Record<PlaceStatus, string> = {
  active:
    "text-[#0d6e3e] bg-[rgba(26,138,82,0.08)] before:bg-[var(--success)]",
  hidden:
    "text-[#7a5200] bg-[rgba(196,138,26,0.08)] before:bg-[#c48a1a]",
};

interface PlaceStatusPillProps {
  status: PlaceStatus;
}

export function PlaceStatusPill({ status }: PlaceStatusPillProps): React.JSX.Element {
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 text-[12px] font-[500] px-2.5 py-0.5 rounded-full whitespace-nowrap",
        "before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:flex-shrink-0",
        PLACE_STATUS_CLASS[status],
      ].join(" ")}
    >
      {PLACE_STATUS_LABELS[status]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Debounce hook
// ---------------------------------------------------------------------------

function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState<T>(value);

  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

// ---------------------------------------------------------------------------
// PlaceRow
// ---------------------------------------------------------------------------

interface PlaceRowProps {
  place: AdminPlace;
  onTakedown: (id: string) => void;
  onRestore: (id: string) => void;
}

export function PlaceRow({ place, onTakedown, onRestore }: PlaceRowProps): React.JSX.Element {
  const status: PlaceStatus = place.isHidden ? "hidden" : "active";

  return (
    <tr className="border-b border-[var(--border)] last:border-b-0 min-h-[44px] transition-colors hover:bg-[var(--accent-weak)]">
      <td
        className="px-3.5 py-2.5 font-mono text-[12px] text-[var(--muted)] max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap"
        title={place.id}
      >
        {place.id}
      </td>
      <td className="px-3.5 py-2.5 text-[13px] font-[500] text-[var(--fg)]">
        {place.name}
      </td>
      <td className="px-3.5 py-2.5 w-[100px]">
        <PlaceStatusPill status={status} />
      </td>
      <td
        className="px-3.5 py-2.5 font-mono text-[12px] text-[var(--muted)] max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap"
        title={place.cityId ?? undefined}
      >
        {place.cityId ?? "—"}
      </td>
      <td
        className="px-3.5 py-2.5 font-mono text-[12px] text-[var(--muted)] max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap"
        title={place.countryId ?? undefined}
      >
        {place.countryId ?? "—"}
      </td>
      <td className="px-3.5 py-2.5 w-[140px]">
        {status === "active" ? (
          <button
            type="button"
            aria-label={`Скрыть место ${place.name}`}
            onClick={() => onTakedown(place.id)}
            className="px-3 py-1.5 bg-[rgba(196,58,42,0.06)] text-[var(--danger)] text-[12px] font-[600] border border-[rgba(196,58,42,0.18)] rounded-[var(--radius-sm)] hover:bg-[rgba(196,58,42,0.12)] transition-colors min-h-[44px] min-w-[44px]"
          >
            Скрыть
          </button>
        ) : (
          <button
            type="button"
            aria-label={`Восстановить место ${place.name}`}
            onClick={() => onRestore(place.id)}
            className="px-3 py-1.5 bg-[var(--surface)] text-[var(--fg)] text-[12px] font-[600] border border-[var(--border)] rounded-[var(--radius-sm)] hover:bg-[var(--surface-2)] transition-colors min-h-[44px] min-w-[44px]"
          >
            Восстановить
          </button>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// PlacesList (page)
// ---------------------------------------------------------------------------

type TakedownAction = { type: "takedown"; id: string; name: string } | null;
type RestoreAction = { type: "restore"; id: string; name: string } | null;
type PendingAction = TakedownAction | RestoreAction;

function resolveStatusFilter(value: string): PlaceStatus | undefined {
  if (value === "active" || value === "hidden") return value;
  return undefined;
}

export function PlacesList(): React.JSX.Element {
  const [searchName, setSearchName] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("");
  const debouncedName = useDebounce(searchName, 300);
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [rows, setRows] = React.useState<AdminPlace[]>([]);
  const [pendingAction, setPendingAction] = React.useState<PendingAction>(null);

  const refineFilters: CrudFilter[] = [];
  if (debouncedName) {
    refineFilters.push({ field: "q", operator: "eq", value: debouncedName });
  }
  const resolvedStatus = resolveStatusFilter(statusFilter);
  if (resolvedStatus) {
    refineFilters.push({ field: "status", operator: "eq", value: resolvedStatus });
  }

  const { query } = useList<AdminPlace>({
    resource: "places",
    filters: refineFilters,
    meta: { cursor },
  });

  const data = query.data;
  const isLoading = query.isLoading;
  const isError = query.isError;
  const refetch = query.refetch;

  React.useEffect(() => {
    if (data?.data) {
      const incoming = data.data as AdminPlace[];
      if (!cursor) {
        setRows(incoming);
      } else {
        setRows((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          return [...prev, ...incoming.filter((p) => !seen.has(p.id))];
        });
      }
    }
  }, [data?.data, cursor]);

  const nextCursor =
    (data as { meta?: { nextCursor?: string | null } } | undefined)?.meta?.nextCursor ?? null;

  function handleSearchChange(value: string): void {
    setSearchName(value);
    setCursor(undefined);
    setRows([]);
  }

  function handleStatusFilterChange(value: string): void {
    setStatusFilter(value);
    setCursor(undefined);
    setRows([]);
  }

  function handleTakedown(id: string): void {
    const place = rows.find((p) => p.id === id);
    if (!place) return;
    setPendingAction({ type: "takedown", id, name: place.name });
  }

  function handleRestore(id: string): void {
    const place = rows.find((p) => p.id === id);
    if (!place) return;
    setPendingAction({ type: "restore", id, name: place.name });
  }

  async function handleConfirmAction(): Promise<void> {
    if (!pendingAction) return;

    const endpoint =
      pendingAction.type === "takedown"
        ? `/admin/places/${pendingAction.id}/takedown`
        : `/admin/places/${pendingAction.id}/restore`;

    if (!dataProvider.custom) throw new Error("dataProvider.custom not implemented");
    await dataProvider.custom({ url: endpoint, method: "post" });

    setRows([]);
    setCursor(undefined);
    void refetch();
  }

  const showEmpty = !isLoading && !isError && rows.length === 0;
  const showTable = !isLoading && rows.length > 0;

  const modalTitle =
    pendingAction?.type === "takedown" ? "Скрыть место" : "Восстановить место";
  const modalDescription =
    pendingAction?.type === "takedown"
      ? `Место «${pendingAction?.name}» будет скрыто из публичного отображения.`
      : `Место «${pendingAction?.name}» будет восстановлено в публичном отображении.`;
  const submitLabel =
    pendingAction?.type === "takedown" ? "Скрыть" : "Восстановить";

  return (
    <div className="flex flex-col min-h-screen">
      <div className="px-6 pt-5 pb-0">
        <h1 className="text-[22px] font-[700] tracking-[-0.3px]">Места</h1>
      </div>

      <div className="sticky top-0 z-[5] bg-[var(--surface)] border-b border-[var(--border)] px-6 py-3 flex items-center gap-3">
        <div className="relative flex-[0_0_320px]">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 stroke-[var(--muted)] stroke-2 fill-none pointer-events-none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            aria-label="Поиск по названию"
            placeholder="Поиск по названию…"
            value={searchName}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full h-9 pl-8 pr-3 border border-[var(--border)] rounded-full text-[13px] font-[var(--font)] text-[var(--fg)] bg-[var(--surface-2)] outline-none transition-colors placeholder:text-[var(--muted)] focus:border-[var(--accent)] focus:bg-[var(--surface)]"
          />
        </div>

        <select
          aria-label="Фильтр по статусу"
          value={statusFilter}
          onChange={(e) => handleStatusFilterChange(e.target.value)}
          className="h-9 px-3 pr-8 border border-[var(--border)] rounded-[var(--radius-sm)] text-[13px] font-[var(--font)] text-[var(--fg)] bg-[var(--surface-2)] cursor-pointer outline-none focus:border-[var(--accent)] appearance-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='rgba(0,0,0,0.52)' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 10px center",
          }}
        >
          <option value="">Статус: все</option>
          <option value="active">Активно</option>
          <option value="hidden">Скрыто</option>
        </select>

        {!isLoading && !isError && rows.length > 0 && (
          <span className="ml-auto text-[13px] text-[var(--muted)] whitespace-nowrap">
            Найдено: {rows.length}
          </span>
        )}
      </div>

      <div className="px-6 pb-6 flex-1">
        {isLoading && (
          <div className="mt-3 bg-[var(--surface)] rounded-[var(--radius)] border border-[var(--border)] overflow-hidden">
            <SkeletonTable columns={6} rows={6} />
          </div>
        )}

        {isError && (
          <div className="mt-3">
            <ErrorBanner
              message="Не удалось загрузить список мест. Проверьте подключение к сети и попробуйте снова."
              onRetry={() => void refetch()}
            />
          </div>
        )}

        {showEmpty && (
          <EmptyState
            message={
              debouncedName
                ? "Места не найдены"
                : "Нет мест для отображения"
            }
          />
        )}

        {showTable && (
          <table className="w-full border-collapse mt-3 bg-[var(--surface)] rounded-[var(--radius)] overflow-hidden border border-[var(--border)]">
            <thead className="bg-[var(--surface-2)]">
              <tr>
                <th
                  scope="col"
                  className="w-[140px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  ID
                </th>
                <th
                  scope="col"
                  className="px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Название
                </th>
                <th
                  scope="col"
                  className="w-[100px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Статус
                </th>
                <th
                  scope="col"
                  className="w-[120px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Город (ID)
                </th>
                <th
                  scope="col"
                  className="w-[120px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Страна (ID)
                </th>
                <th
                  scope="col"
                  className="w-[140px] px-3.5 py-2.5 text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.5px] text-left border-b border-[var(--border)] whitespace-nowrap"
                >
                  Действия
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((place) => (
                <PlaceRow
                  key={place.id}
                  place={place}
                  onTakedown={handleTakedown}
                  onRestore={handleRestore}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <CursorPagination
        nextCursor={nextCursor}
        onLoadMore={() => setCursor(nextCursor ?? undefined)}
        isLoading={isLoading}
      />

      <ConfirmModal
        open={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onSubmit={handleConfirmAction}
        title={modalTitle}
        confirmTitle="Подтвердите действие"
        confirmDescription={modalDescription}
        submitLabel={submitLabel}
        submitVariant={pendingAction?.type === "takedown" ? "destructive" : "default"}
        doubleConfirm={false}
        requireReason={false}
        footerNote="Действие зафиксируется в Audit Log"
      />
    </div>
  );
}
